import React from 'react';

import * as api from '@gitbook/api-client';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    DataTable,
    type DataTableColumns,
    DataTableFooter,
    DataTableLayout,
    DataTableRoot,
    Description,
    EmptyState,
    EmptyStateIcon,
    Heading,
    Pagination,
    Panel,
    PanelBody,
} from '@gitbook/spine-react';
import { getChangeRequestSubject } from '@gitbook/util-format';

import { NavigationLink } from '../../navigation';
import { apiResource, useSuspensePaginatedAPIQuery } from '../../state';
import { ChangeRequestStatusBadge } from '../ChangeRequest';
import { SpaceLink } from '../Space';

/**
 * Table to display change requests.
 */
export function ClusterChangeRequestsTable(props: { organizationId: string; clusterId: string }) {
    const { organizationId, clusterId } = props;

    const { data, next, previous, range } = useSuspensePaginatedAPIQuery(
        apiResource('listChangeRequestsForOrganization', {
            organizationId,
            conversationsIssuesCluster: clusterId,
            status: [
                api.ChangeRequestStatus.Open,
                api.ChangeRequestStatus.Draft,
                api.ChangeRequestStatus.Merged,
                api.ChangeRequestStatus.Archived,
            ],
        })
    );

    const columns = React.useMemo<DataTableColumns<api.ChangeRequest>>(() => {
        return [
            {
                key: 'label',
                label: 'Change request',
                isRowHeader: true,

                cell: ({ item }) => (
                    <div className="flex items-center gap-2">
                        <ChangeRequestStatusBadge status={item.status} />
                        <NavigationLink
                            to={{
                                screen: {
                                    type: 'change_requests',
                                    organization: organizationId,
                                    selected: {
                                        spaceId: item.space,
                                        changeRequestNumber: item.number,
                                    },
                                },
                            }}
                            className="text-ui-action"
                        >
                            #{item.number} {getChangeRequestSubject(item)}
                        </NavigationLink>
                    </div>
                ),
            },
            {
                key: 'space',
                label: 'Space',
                cell: ({ item }) => <SpaceLink spaceId={item.space} />,
            },
            {
                key: 'createdAt',
                label: 'Created',
                alignment: 'end',
                cell: ({ item }) => (
                    <div className="text-muted text-ui-small">
                        <DateShortRelative value={item.createdAt} onlyDate={false} />
                    </div>
                ),
            },
        ];
    }, [organizationId]);

    if (data.items.length === 0) {
        return (
            <Panel>
                <PanelBody>
                    <EmptyState>
                        <EmptyStateIcon>
                            <Icon.ChangeRequest />
                        </EmptyStateIcon>
                        <Heading>No change requests</Heading>
                        <Description>
                            No change requests have been generated for this cluster of issues yet.
                        </Description>
                    </EmptyState>
                </PanelBody>
            </Panel>
        );
    }

    return (
        <DataTableRoot>
            <DataTableLayout>
                <DataTable columns={columns} items={data.items} />
                <DataTableFooter>
                    <Pagination range={range} next={next} previous={previous} />
                </DataTableFooter>
            </DataTableLayout>
        </DataTableRoot>
    );
}
